/**
 * @file DeleteReauth.tsx
 * @brief The extra proof Delete account and Delete company ask for from a
 *        Google / Apple / Microsoft account (no password) signed in over 10
 *        minutes ago (B33, 26-10-2026).
 *
 * @details
 * 403 REAUTH_REQUIRED says which proof:
 * - `twoFactorAllowed: true` (2FA on): a code box inside the delete form,
 *   "Use a backup code", and the delete is sent again with `twoFactorCode`
 *   or `backupCode`. A wrong code is 400 TWO_FACTOR_INVALID_CODE with
 *   `attemptsLeft`, the lock 429 TWO_FACTOR_LOCKED: both shown under the box,
 *   never a sign-out, never retried (the API client only signs out on 401
 *   UNAUTHORIZED; mutations do not retry).
 * - otherwise: sign in again (`ReauthSignIn`, below the form, since the code
 *   step is a form of its own). It signs in with `reauth`, so the delete
 *   screen stays mounted; if that sign-in answers with the 2FA code (or
 *   setup) step, the step shows here, then the delete is sent once with the
 *   fresh session.
 * State and error handling: `useDeleteReauth.ts`. Codes are never stored
 * or logged.
 *
 * @references
 * - docs/product/two-step-verification.md "Re-authentication with 2FA (B33)"
 *
 * @author DES (prepared with Claude)
 * @date 26-10-2026
 */

import { useId } from "react";
import { SocialSignIn } from "@/features/auth/SocialSignIn";
import { CodeField, TwoFactorSignInStep } from "@/features/auth/twoFactor/TwoFactorSteps";
import { challengeFromError } from "@/features/auth/twoFactor/twoFactorApi";
import { useAuth } from "@/shared/auth/auth-context";
import { toast } from "@/shared/hooks/useToast";
import type { AuthUser } from "@/shared/types/api";
import type { DeleteReauth } from "./useDeleteReauth";

/** Code mode: inside the delete form, so Enter / the delete button sends it. */
export function ReauthCodeField({ reauth, busy }: { reauth: DeleteReauth; busy: boolean }) {
  const id = useId();
  if (reauth.mode !== "code") return null;
  return (
    <div className="max-w-sm space-y-3">
      <p className="text-sm">
        {reauth.backup
          ? "For your security, enter one of the backup codes you saved."
          : "For your security, enter the code from your authenticator app."}
      </p>
      <CodeField
        key={reauth.backup ? "backup" : "app"}
        id={id}
        label={reauth.backup ? "Backup code" : "Code"}
        backup={reauth.backup}
        value={reauth.code}
        onChange={(v) => {
          reauth.setCode(v);
          if (reauth.error) reauth.setError(null);
        }}
        disabled={busy}
        inputRef={reauth.inputRef}
      />
      {reauth.error ? (
        <p role="alert" className="text-xs text-destructive">
          {reauth.error}
        </p>
      ) : null}
      <button
        type="button"
        className="text-sm font-medium text-brand hover:underline"
        disabled={busy}
        onClick={() => {
          reauth.setBackup(!reauth.backup);
          reauth.setCode("");
          reauth.setError(null);
        }}
      >
        {reauth.backup ? "Use a code from your app" : "Use a backup code"}
      </button>
    </div>
  );
}

/**
 * Sign-in mode: outside the delete form. `then` finishes the sentence
 * ("your account is deleted").
 */
export function ReauthSignIn({
  reauth,
  busy,
  then,
  onSignedIn,
}: {
  reauth: DeleteReauth;
  busy: boolean;
  then: string;
  onSignedIn: (user: AuthUser) => void;
}) {
  const { twoFactorChallenge } = useAuth();
  if (reauth.mode !== "signIn") return null;
  if (twoFactorChallenge) {
    return (
      <div className="max-w-sm">
        <TwoFactorSignInStep onSignedIn={(u) => onSignedIn(u)} backLabel="Cancel" />
      </div>
    );
  }
  return (
    <>
      <p className="mb-3 text-sm">For your security, please sign in again, then {then}.</p>
      <div className="max-w-sm">
        <SocialSignIn
          reauth
          disabled={busy}
          onSignedIn={onSignedIn}
          onError={(err) => {
            // The 2FA step replaces the buttons above; nothing to toast.
            if (challengeFromError(err)) return;
            toast({
              variant: "destructive",
              title: "Sign-in failed",
              description: err instanceof Error ? err.message : "Please try again.",
            });
          }}
        />
      </div>
    </>
  );
}
